import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { sendAsync } from "../../../app/applicationSlice";
import { RootDispatch, RootState, RootThunkApi } from "../../../app/store";
import * as R from "../../../models/recipe";
import { Recipe } from "../../../models/recipe";
import { Treatment, TreatmentNotice } from "../../../models/treatment";
import * as SR from "../../../services/recipe";
import * as ST from "../../../services/treatment";

interface RecipeEditorState {
  treatment?: Treatment;
  recipes: Recipe[];
}

const initialState = {
  recipes: [],
} as RecipeEditorState;

function reloadRecipeList({
  dispatch,
  getState,
}: {
  dispatch: RootDispatch;
  getState: () => RootState;
}) {
  const treatment = selectTreatment(getState());
  if (treatment) {
    dispatch(getRecipeList(treatment.id));
  }
}

export const createOrUpdateRecipe = createAsyncThunk<
  string,
  Recipe,
  RootThunkApi<string>
>("recipeListEditor/createOrUpdateRecipe", async (recipe, api) => {
  return sendAsync(SR.createOrUpdateRecipe(recipe), api).then((data) => {
    return data?.toString() || "";
  });
});

export const getRecipeList = createAsyncThunk<
  Recipe[],
  string,
  RootThunkApi<Recipe[]>
>("recipeListEditor/getRecipeListStatus", async (treatmentId, api) => {
  return sendAsync(SR.getRecipeList(treatmentId), api).then(
    (data: Record<string, never>) =>
      _.chain(data.records)
        .map((r) => R.fromJson(r))
        .filter((r) => r.category !== R.RecipeCategory.Extras)
        .value()
  );
});

export const removeRecipe = createAsyncThunk<void, string, RootThunkApi<void>>(
  "recipeListEditor/removeRecipeStatus",
  async (recipeId, api) => {
    return sendAsync(SR.removeRecipe(recipeId), api).then(() => {
      reloadRecipeList(api);
    });
  }
);

export const getTreatmentNotice = createAsyncThunk<
  TreatmentNotice,
  string,
  RootThunkApi<TreatmentNotice>
>("recipeListEditor/getTreatmentNoticeStatus", async (patientId, api) => {
  return sendAsync(ST.getTreatmentNotice(patientId), api);
});

export const getTab = createAsyncThunk<void, any, RootThunkApi>(
  "auth/getTab",
  async (params, api) => {
    return sendAsync(ST.getTab(params), api);
  }
);

export const getCutTab = createAsyncThunk<
  void,
  ST.tabStateParams,
  RootThunkApi
>("auth/getCutTab", async (body, api) => {
  return sendAsync(ST.getCutTab(body), api);
});

export const finishTreatment = createAsyncThunk<
  void,
  string,
  RootThunkApi<void>
>("recipeListEditor/finishTreatmentStatus", async (treatmentId, api) => {
  return sendAsync(SR.finishTreatment(treatmentId), api);
});


export const getTreatments = createAsyncThunk<
  void,
  ST.GetTreatmentDetailParams,
  RootThunkApi<void>
>("diagnosis/copyTreatmentStatus", async (params, api) => {
  return sendAsync(ST.getTreatment(params), api);
});

const recipeEditorSlice = createSlice({
  name: "recipeEditor",
  initialState,
  reducers: {
    setRecipeEditorTreatment: (
      state,
      action: PayloadAction<Treatment | undefined>
    ) => {
      state.treatment = action.payload;
    },
    updateRecipe: (state, action: PayloadAction<Recipe>) => {
      state.recipes = state.recipes.map((r) =>
        r.id === action.payload.id ? action.payload : r
      );
    },
    resetRecipeList: (state) => {
      state.treatment = undefined;
      state.recipes = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRecipeList.fulfilled, (state, action) => {
      state.recipes = action.payload;
    });
  },
});

export const selectTreatment = (state: RootState): Treatment | undefined =>
  state.recipeListEditor.treatment;

export const selectRecipes = (state: RootState): Recipe[] =>
  state.recipeListEditor.recipes;

export const {
  setRecipeEditorTreatment,
  updateRecipe,
  resetRecipeList,
} = recipeEditorSlice.actions;

export default recipeEditorSlice.reducer;
